/**
 * S08 — Submit Opportunity Request.
 *
 * Multi-step modal with full client-side validation. On submit:
 *   - Creates a new opportunity overlay (via TenantModeContext)
 *   - Creates a deployment-request record at stage 1
 *   - Writes an audit-log entry
 *   - Shows the confirmation experience with the freshly minted IDs
 *
 * Steps:
 *   1. Opportunity   — title, dept, BU, owner, business problem
 *   2. Approach      — proposed solution, recommended approach + tech
 *   3. Value & Risk  — estimated value, effort, risk, compliance impact + considerations, success metrics
 *   4. Review        — read-only confirmation before submission
 *   5. Confirmation  — ids + close
 */
import React, { useMemo, useState } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCircle2,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import {
  OPPORTUNITY_DEPARTMENTS,
  OPPORTUNITY_BUSINESS_UNITS,
  OPPORTUNITY_EFFORT_OPTIONS,
  OPPORTUNITY_RISK_OPTIONS,
  OPPORTUNITY_AI_APPROACHES,
} from "@/lib/demoData";
import { Modal } from "@/components/ui/Modal";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { useAuth } from "@/contexts/AuthContext";
import { fmtAud } from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";

const STEPS = [
  { id: 1, label: "Opportunity" },
  { id: 2, label: "Approach" },
  { id: 3, label: "Value & Risk" },
  { id: 4, label: "Review" },
];

const COMPLIANCE_OPTIONS = [
  { value: "low", label: "Low — internal only" },
  { value: "med", label: "Medium — customer-facing rationale" },
  { value: "high", label: "High — regulated decisioning" },
];

const emptyForm = {
  title: "",
  department: "",
  businessUnit: "",
  owner: "",
  businessProblem: "",
  proposedSolution: "",
  recommendedApproach: "",
  recommendedTech: [],
  estAnnualRoiAud: "",
  effort: "",
  risk: "",
  complianceImpact: "",
  complianceConsiderations: "",
  successMetric: "",
  requiredApprovals: ["IT Security", "CAIO"],
};

/**
 * Build the initial form snapshot from props. Pure — runs once per
 * SubmitForm mount, which only happens when the modal opens. By moving
 * state ownership into SubmitForm we eliminate the entire reset-on-open
 * effect and the React-19 set-state-in-effect rule no longer applies.
 */
function buildInitialForm(prefill, userName) {
  if (!prefill) return { ...emptyForm, owner: userName || "" };
  return {
    ...emptyForm,
    title: prefill.title || "",
    department: prefill.department || "",
    businessUnit: prefill.businessUnit || "",
    owner: prefill.owner || userName || "",
    businessProblem: prefill.businessProblem || "",
    proposedSolution: prefill.proposedSolution || "",
    recommendedApproach: prefill.recommendedApproach || "",
    recommendedTech: prefill.recommendedTech || [],
    estAnnualRoiAud: String(prefill.estimatedValueAud || ""),
    effort: prefill.effort || "",
    risk: prefill.risk || "",
    complianceImpact: prefill.complianceImpact || "",
    complianceConsiderations: prefill.complianceConsiderations || "",
    successMetric: (prefill.successMetrics && prefill.successMetrics[0]) || "",
    requiredApprovals: prefill.requiredApprovals || ["IT Security", "CAIO"],
  };
}

export default function SubmitOpportunityRequest({ open, onClose, prefill }) {
  // The <Modal /> primitive owns mount-gating + backdrop + Esc + ARIA,
  // so SubmitForm is only instantiated while open === true and its local
  // state always starts fresh on each open. No reset-from-effect needed.
  return (
    <Modal
      open={open}
      onClose={onClose}
      widthClass="max-w-[720px]"
      testId="submit-opp-modal"
      bodyPadded={false}
    >
      <SubmitForm onClose={onClose} prefill={prefill} />
    </Modal>
  );
}

function SubmitForm({ onClose, prefill }) {
  // Phase 3b — opportunities list is read from REST (used by the wizard
  // for duplicate-title pre-check + autocomplete suggestions). The
  // mutation submit still comes from the tenant context.
  const { items: opportunities = [] } = useEntityList("/api/opportunities");
  const { submitOpportunityRequest } = useTenantData();
  const { user } = useAuth();

  // Tenant-aware department + BU lists derived from existing
  // opportunities (Acme shows banking, Northwind shows insurance). Fall
  // back to the static lists for an empty-tenant edge case.
  const departments = useMemo(() => {
    const set = new Set();
    opportunities.forEach((o) => o.department && set.add(o.department));
    return set.size ? Array.from(set).sort() : OPPORTUNITY_DEPARTMENTS;
  }, [opportunities]);
  const businessUnits = useMemo(() => {
    const set = new Set();
    opportunities.forEach((o) => o.businessUnit && set.add(o.businessUnit));
    return set.size ? Array.from(set).sort() : OPPORTUNITY_BUSINESS_UNITS;
  }, [opportunities]);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => buildInitialForm(prefill, user?.name));
  const [touched, setTouched] = useState({});
  const [result, setResult] = useState(null);

  // ── Validation ─────────────────────────────────────────────────────────
  const errors = useMemo(() => validate(form), [form]);
  const stepValid = useMemo(() => isStepValid(step, errors), [step, errors]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));
  const showError = (k) => touched[k] && errors[k];

  const handleNext = () => {
    if (!stepValid) {
      // mark all fields on this step as touched to surface errors
      setTouched((t) => ({ ...t, ...stepTouched(step) }));
      return;
    }
    setStep((s) => Math.min(STEPS.length, s + 1));
  };
  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async () => {
    if (Object.keys(errors).length || submitting) return;
    const approach = OPPORTUNITY_AI_APPROACHES.find(
      (a) => a.label === form.recommendedApproach,
    );
    const numericValue = Number(form.estAnnualRoiAud) || 0;
    // UI effort codes (S/M/L) drifted from the backend's `Literal["low","med","high"]`
    // enum — translate here. Risk + complianceImpact already align so they pass through.
    const effortApi = { S: "low", M: "med", L: "high" }[form.effort] || form.effort;
    const payload = {
      ...form,
      effort: effortApi,
      estAnnualRoiAud: numericValue,
      estimatedValueAud: numericValue,  // canonical name on the API side
      recommendedTech: approach?.tech || [],
      successMetrics: form.successMetric ? [form.successMetric] : [],
      requiredApprovals:
        form.risk === "high"
          ? ["IT Security", "Legal & Compliance", "CAIO", "Board Risk Sub-Committee"]
          : form.risk === "med"
            ? ["IT Security", "Legal & Compliance", "CAIO"]
            : ["IT Security", "CAIO"],
      actorId: user?.id,
      riskAssessment:
        form.risk === "high"
          ? "Decisions inform regulated outcomes. Requires CAIO + Legal sign-off."
          : form.risk === "med"
            ? "Customer-impacting outputs require disclosure language review."
            : "Internal-only assistive output. Standard governance suffices.",
    };
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await submitOpportunityRequest(payload);
      setResult(r);
    } catch (e) {
      setSubmitError(e?.message || "Submit failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <Confirmation result={result} onClose={onClose} />;

  return (
    <>
      <Header onClose={onClose} step={step} />
      <Stepper step={step} />
      <div className="px-6 py-5">
        {step === 1 && (
          <StepOpportunity
            form={form}
            setField={setField}
            markTouched={markTouched}
            showError={showError}
            errors={errors}
            departments={departments}
            businessUnits={businessUnits}
          />
        )}
        {step === 2 && (
          <StepApproach
            form={form}
            setField={setField}
            markTouched={markTouched}
            showError={showError}
            errors={errors}
          />
        )}
        {step === 3 && (
          <StepValueRisk
            form={form}
            setField={setField}
            markTouched={markTouched}
            showError={showError}
            errors={errors}
          />
        )}
        {step === 4 && <StepReview form={form} />}
        {submitError && (
          <p
            className="mt-3 text-meta text-risk-high"
            data-testid="submit-opp-server-error"
          >
            {submitError}
          </p>
        )}
      </div>
      <Footer
        step={step}
        stepValid={stepValid}
        onBack={handleBack}
        onNext={handleNext}
        onSubmit={handleSubmit}
        onClose={onClose}
        submitting={submitting}
      />
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Header / Stepper / Footer
// ───────────────────────────────────────────────────────────────────────────
function Header({ onClose, step }) {
  return (
    <div className="px-6 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-border-subtle">
      <div>
        <p className="eyebrow">Identify</p>
        <h2 className="text-h2 text-ink-primary mt-0.5">Submit AI opportunity</h2>
        <p className="text-meta text-ink-secondary mt-1">
          Step {step} of {STEPS.length} — {STEPS[step - 1].label}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-ink-secondary hover:bg-inset"
        aria-label="Close"
        data-testid="submit-opp-close"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <ol className="px-6 pt-4 pb-1 flex items-center gap-2" data-testid="submit-opp-stepper">
      {STEPS.map((s) => {
        const active = s.id === step;
        const done = s.id < step;
        return (
          <li key={s.id} className="flex items-center gap-2 flex-1 min-w-0">
            <span
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold tabular-nums shrink-0",
                done
                  ? "bg-tralenex-600 text-white"
                  : active
                    ? "bg-gold-600 text-white"
                    : "bg-inset text-ink-secondary",
              )}
            >
              {s.id}
            </span>
            <span
              className={cn(
                "text-meta font-semibold truncate",
                active ? "text-ink-primary" : "text-ink-secondary",
              )}
            >
              {s.label}
            </span>
            {s.id < STEPS.length && (
              <span className={cn("flex-1 h-px", done ? "bg-tralenex-600" : "bg-border-subtle")} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function Footer({ step, stepValid, onBack, onNext, onSubmit, onClose, submitting }) {
  return (
    <div className="px-6 py-3 border-t border-border-subtle bg-inset/30 flex items-center gap-2">
      <button
        type="button"
        onClick={onClose}
        className="h-9 px-3 rounded-md text-sm font-medium text-ink-secondary hover:bg-inset"
        data-testid="submit-opp-cancel"
      >
        Cancel
      </button>
      <span className="flex-1" />
      {step > 1 && (
        <button
          type="button"
          onClick={onBack}
          className="h-9 px-3 rounded-md text-sm font-medium text-ink-primary hover:bg-inset inline-flex items-center gap-1.5"
          data-testid="submit-opp-back"
        >
          <ChevronLeft size={13} /> Back
        </button>
      )}
      {step < STEPS.length && (
        <button
          type="button"
          onClick={onNext}
          className={cn(
            "h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-colors",
            stepValid
              ? "bg-tralenex-600 text-white hover:bg-tralenex-700"
              : "bg-tralenex-600/40 text-white cursor-not-allowed",
          )}
          data-testid="submit-opp-next"
        >
          Continue <ChevronRight size={13} />
        </button>
      )}
      {step === STEPS.length && (
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className={cn(
            "h-9 px-4 rounded-md text-sm font-semibold bg-tralenex-600 text-white hover:bg-tralenex-700 inline-flex items-center gap-2",
            submitting && "opacity-70 cursor-not-allowed",
          )}
          data-testid="submit-opp-submit"
        >
          <Send size={13} /> {submitting ? "Submitting…" : "Submit for governance"}
        </button>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Step 1 — Opportunity
// ───────────────────────────────────────────────────────────────────────────
function StepOpportunity({ form, setField, markTouched, showError, errors, departments, businessUnits }) {
  return (
    <div className="space-y-4">
      <Field label="Opportunity title" required error={showError("title") && errors.title}>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setField("title", e.target.value)}
          onBlur={() => markTouched("title")}
          placeholder="e.g. Mortgage origination triage copilot"
          className={inputCls(showError("title"))}
          data-testid="submit-opp-title"
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Department" required error={showError("department") && errors.department}>
          <select
            value={form.department}
            onChange={(e) => setField("department", e.target.value)}
            onBlur={() => markTouched("department")}
            className={inputCls(showError("department"))}
            data-testid="submit-opp-department"
          >
            <option value="">Select department…</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Business unit" required error={showError("businessUnit") && errors.businessUnit}>
          <select
            value={form.businessUnit}
            onChange={(e) => setField("businessUnit", e.target.value)}
            onBlur={() => markTouched("businessUnit")}
            className={inputCls(showError("businessUnit"))}
            data-testid="submit-opp-businessUnit"
          >
            <option value="">Select business unit…</option>
            {businessUnits.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Business owner" required error={showError("owner") && errors.owner}>
        <input
          type="text"
          value={form.owner}
          onChange={(e) => setField("owner", e.target.value)}
          onBlur={() => markTouched("owner")}
          placeholder="e.g. J. Patel"
          className={inputCls(showError("owner"))}
          data-testid="submit-opp-owner"
        />
      </Field>

      <Field
        label="Business problem"
        required
        hint="What pain point is this solving? Keep to 1–3 sentences."
        error={showError("businessProblem") && errors.businessProblem}
      >
        <textarea
          rows={4}
          value={form.businessProblem}
          onChange={(e) => setField("businessProblem", e.target.value)}
          onBlur={() => markTouched("businessProblem")}
          className={inputCls(showError("businessProblem"))}
          placeholder="Manual review across multiple systems creates a 4-day cycle…"
          data-testid="submit-opp-problem"
        />
      </Field>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Step 2 — Approach
// ───────────────────────────────────────────────────────────────────────────
function StepApproach({ form, setField, markTouched, showError, errors }) {
  return (
    <div className="space-y-4">
      <Field
        label="Proposed solution"
        required
        error={showError("proposedSolution") && errors.proposedSolution}
        hint="Briefly describe how the solution works end-to-end."
      >
        <textarea
          rows={4}
          value={form.proposedSolution}
          onChange={(e) => setField("proposedSolution", e.target.value)}
          onBlur={() => markTouched("proposedSolution")}
          className={inputCls(showError("proposedSolution"))}
          placeholder="Retrieval-augmented LLM integrated with…"
          data-testid="submit-opp-solution"
        />
      </Field>

      <Field
        label="Recommended AI approach"
        required
        error={showError("recommendedApproach") && errors.recommendedApproach}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {OPPORTUNITY_AI_APPROACHES.map((a) => {
            const active = form.recommendedApproach === a.label;
            return (
              <button
                key={a.code}
                type="button"
                onClick={() => {
                  setField("recommendedApproach", a.label);
                  markTouched("recommendedApproach");
                }}
                className={cn(
                  "text-left rounded-md border p-3 transition-colors",
                  active
                    ? "border-tralenex-600 bg-tralenex-50"
                    : "border-border-subtle hover:border-tralenex-300 hover:bg-inset",
                )}
                data-testid={`submit-opp-approach-${a.code}`}
              >
                <p className="text-sm font-semibold text-ink-primary">{a.label}</p>
                <p className="mt-1 text-meta text-ink-secondary">{a.tech.join(" · ")}</p>
              </button>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Step 3 — Value & Risk
// ───────────────────────────────────────────────────────────────────────────
function StepValueRisk({ form, setField, markTouched, showError, errors }) {
  return (
    <div className="space-y-4">
      <Field
        label="Estimated annual value (AUD)"
        required
        error={showError("estAnnualRoiAud") && errors.estAnnualRoiAud}
        hint="Numeric — savings or revenue uplift per year."
      >
        <input
          type="number"
          min="0"
          step="10000"
          value={form.estAnnualRoiAud}
          onChange={(e) => setField("estAnnualRoiAud", e.target.value)}
          onBlur={() => markTouched("estAnnualRoiAud")}
          placeholder="e.g. 1200000"
          className={inputCls(showError("estAnnualRoiAud"))}
          data-testid="submit-opp-value"
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Effort" required error={showError("effort") && errors.effort}>
          <select
            value={form.effort}
            onChange={(e) => setField("effort", e.target.value)}
            onBlur={() => markTouched("effort")}
            className={inputCls(showError("effort"))}
            data-testid="submit-opp-effort"
          >
            <option value="">Select effort…</option>
            {OPPORTUNITY_EFFORT_OPTIONS.map((e) => (
              <option key={e.code} value={e.code}>
                {e.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Risk" required error={showError("risk") && errors.risk}>
          <select
            value={form.risk}
            onChange={(e) => setField("risk", e.target.value)}
            onBlur={() => markTouched("risk")}
            className={inputCls(showError("risk"))}
            data-testid="submit-opp-risk"
          >
            <option value="">Select risk…</option>
            {OPPORTUNITY_RISK_OPTIONS.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field
        label="Compliance impact"
        required
        error={showError("complianceImpact") && errors.complianceImpact}
      >
        <select
          value={form.complianceImpact}
          onChange={(e) => setField("complianceImpact", e.target.value)}
          onBlur={() => markTouched("complianceImpact")}
          className={inputCls(showError("complianceImpact"))}
          data-testid="submit-opp-complianceImpact"
        >
          <option value="">Select impact…</option>
          {COMPLIANCE_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Compliance considerations" hint="Optional — frameworks, residency, PII notes">
        <textarea
          rows={3}
          value={form.complianceConsiderations}
          onChange={(e) => setField("complianceConsiderations", e.target.value)}
          className={inputCls(false)}
          placeholder="e.g. APRA CPS 234, ASIC RG 271, Privacy Act AU"
          data-testid="submit-opp-complianceNotes"
        />
      </Field>

      <Field label="Primary success metric" required error={showError("successMetric") && errors.successMetric}>
        <input
          type="text"
          value={form.successMetric}
          onChange={(e) => setField("successMetric", e.target.value)}
          onBlur={() => markTouched("successMetric")}
          placeholder="e.g. Cycle time reduction ≥ 60%"
          className={inputCls(showError("successMetric"))}
          data-testid="submit-opp-metric"
        />
      </Field>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Step 4 — Review
// ───────────────────────────────────────────────────────────────────────────
function StepReview({ form }) {
  return (
    <div className="space-y-4" data-testid="submit-opp-review">
      <div className="p-4 rounded-md border border-border-subtle bg-inset/30">
        <p className="eyebrow">Opportunity</p>
        <p className="text-h3 text-ink-primary mt-1">{form.title}</p>
        <p className="text-meta text-ink-secondary mt-0.5">
          {form.department} · {form.businessUnit} · owner {form.owner}
        </p>
      </div>
      <ReviewBlock title="Business problem">{form.businessProblem}</ReviewBlock>
      <ReviewBlock title="Proposed solution">{form.proposedSolution}</ReviewBlock>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ReviewKv label="Recommended approach" value={form.recommendedApproach} />
        <ReviewKv label="Estimated annual value" value={fmtAud(Number(form.estAnnualRoiAud) || 0)} />
        <ReviewKv label="Effort" value={OPPORTUNITY_EFFORT_OPTIONS.find((e) => e.code === form.effort)?.label} />
        <ReviewKv label="Risk" value={OPPORTUNITY_RISK_OPTIONS.find((r) => r.code === form.risk)?.label} />
        <ReviewKv label="Compliance impact" value={COMPLIANCE_OPTIONS.find((c) => c.value === form.complianceImpact)?.label} />
        <ReviewKv label="Primary success metric" value={form.successMetric} />
      </div>
      <div className="p-3 rounded-md bg-tralenex-50 border border-tralenex-300/40 flex items-start gap-2">
        <Sparkles size={14} className="text-tralenex-700 mt-0.5 shrink-0" />
        <p className="text-meta text-ink-primary">
          On submission, ALGOS will create a deployment request at stage 1, write
          an audit-log entry, and notify the required approvers based on risk.
        </p>
      </div>
    </div>
  );
}

function ReviewBlock({ title, children }) {
  return (
    <div>
      <p className="eyebrow">{title}</p>
      <p className="mt-1 text-sm text-ink-primary leading-relaxed">{children || "—"}</p>
    </div>
  );
}
function ReviewKv({ label, value }) {
  return (
    <div className="text-sm">
      <p className="eyebrow">{label}</p>
      <p className="mt-0.5 text-ink-primary">{value || "—"}</p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Confirmation
// ───────────────────────────────────────────────────────────────────────────
function Confirmation({ result, onClose }) {
  return (
    <div className="px-6 py-8 text-center" data-testid="submit-opp-confirmation">
      <div className="mx-auto h-12 w-12 inline-flex items-center justify-center rounded-full bg-risk-low/10 text-risk-low">
        <CheckCircle2 size={26} />
      </div>
      <h2 className="mt-4 text-h2 text-ink-primary">Opportunity submitted for governance</h2>
      <p className="mt-1 text-ink-secondary text-[15px]">
        ALGOS has created the artefacts below. The required approvers have been notified.
      </p>
      <dl className="mt-5 mx-auto max-w-md text-left text-sm space-y-2">
        <ResultRow label="Opportunity id" value={result.opportunity.id} testId="submit-opp-result-opp-id" />
        <ResultRow label="Deployment request id" value={result.request.id} testId="submit-opp-result-req-id" />
        <ResultRow label="Stage" value={`1 — ${"Submitted"}`} />
        <ResultRow label="Audit entry" value={result.audit.action} testId="submit-opp-result-audit" />
      </dl>
      <div className="mt-6 flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-9 px-4 rounded-md text-sm font-semibold bg-tralenex-600 text-white hover:bg-tralenex-700"
          data-testid="submit-opp-confirmation-close"
        >
          View on radar
        </button>
      </div>
    </div>
  );
}

function ResultRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border-subtle py-1.5">
      <dt className="text-ink-secondary">{label}</dt>
      <dd className="font-mono text-ink-primary" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Validation
// ───────────────────────────────────────────────────────────────────────────
function validate(f) {
  const e = {};
  if (!f.title.trim()) e.title = "Title is required.";
  else if (f.title.trim().length < 6) e.title = "Title must be at least 6 characters.";
  if (!f.department) e.department = "Select a department.";
  if (!f.businessUnit) e.businessUnit = "Select a business unit.";
  if (!f.owner.trim()) e.owner = "Owner is required.";
  if (!f.businessProblem.trim()) e.businessProblem = "Describe the business problem.";
  else if (f.businessProblem.trim().length < 20) e.businessProblem = "Add a little more detail (≥ 20 characters).";
  if (!f.proposedSolution.trim()) e.proposedSolution = "Describe the proposed solution.";
  else if (f.proposedSolution.trim().length < 20)
    e.proposedSolution = "Add a little more detail (≥ 20 characters).";
  if (!f.recommendedApproach) e.recommendedApproach = "Pick a recommended approach.";
  if (!String(f.estAnnualRoiAud).trim()) e.estAnnualRoiAud = "Provide an estimated annual value.";
  else if (Number(f.estAnnualRoiAud) <= 0) e.estAnnualRoiAud = "Value must be greater than zero.";
  if (!f.effort) e.effort = "Pick an effort sizing.";
  if (!f.risk) e.risk = "Pick a risk band.";
  if (!f.complianceImpact) e.complianceImpact = "Pick a compliance impact.";
  if (!f.successMetric.trim()) e.successMetric = "Provide a primary success metric.";
  return e;
}

function isStepValid(step, errors) {
  const fieldsByStep = {
    1: ["title", "department", "businessUnit", "owner", "businessProblem"],
    2: ["proposedSolution", "recommendedApproach"],
    3: ["estAnnualRoiAud", "effort", "risk", "complianceImpact", "successMetric"],
    4: [], // review-only
  };
  return fieldsByStep[step].every((k) => !errors[k]);
}

function stepTouched(step) {
  const fieldsByStep = {
    1: ["title", "department", "businessUnit", "owner", "businessProblem"],
    2: ["proposedSolution", "recommendedApproach"],
    3: ["estAnnualRoiAud", "effort", "risk", "complianceImpact", "successMetric"],
    4: [],
  };
  return Object.fromEntries(fieldsByStep[step].map((k) => [k, true]));
}

// ───────────────────────────────────────────────────────────────────────────
// Small UI helpers
// ───────────────────────────────────────────────────────────────────────────
function Field({ label, hint, required, error, children }) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-ink-primary">
          {label}
          {required && <span className="text-risk-high"> *</span>}
        </span>
        {hint && <span className="text-meta text-ink-disabled truncate">{hint}</span>}
      </span>
      <span className="mt-1 block">{children}</span>
      {error && (
        <span className="mt-1 inline-flex items-center gap-1 text-meta text-risk-high">
          <AlertCircle size={11} /> {error}
        </span>
      )}
    </label>
  );
}

function inputCls(hasError) {
  return cn(
    "block w-full h-9 px-3 rounded-md border bg-raised text-sm text-ink-primary placeholder:text-ink-disabled outline-none transition-colors",
    hasError
      ? "border-risk-high focus:ring-2 focus:ring-risk-high/30"
      : "border-border-subtle focus:border-tralenex-300 focus:ring-2 focus:ring-tralenex-200",
    "min-h-[36px]",
  );
}
